'use client';

import ListTabs from '@/components/molecules/ForexList/ListTabs';
import ForexListSkeleton from './ForexListSkeleton';
import { Section } from '@/components/atoms/Section';
import { mobileQuotesColumns, mobilePerformanceColumns } from '@/components/molecules/ForexList/columns';
import dynamic from 'next/dynamic';
import { useCallback, useRef, useState } from 'react';
import CurrencySearchInput from '@/components/molecules/CurrencySearchInput';
import { useQuery } from '@tanstack/react-query';
import { getForexList } from './getForexList';
import useGetUserSave from '@/components/atoms/ReactTable/hooks/useGetUserSave';
import { saveColumn } from '@/components/atoms/ReactTable/columns';
import { SubTab } from './config';
import { CustomEventName, GTMEventKey, sendGTM } from '@/utils/gtm';
import { QuotesCodeMapping } from '@/constants/quote/quote';
import useFixedCard from '@/store/useFixedCard';

type DFormat = {
  [QuotesCodeMapping.SYMBOL]: string;
  [QuotesCodeMapping.TRADE_TIME]: number;
  [QuotesCodeMapping.CHINESE_NAME]: string;
  [QuotesCodeMapping.ENGLISH_NAME]: string;
  [QuotesCodeMapping.BID]: string;
  [QuotesCodeMapping.ASK]: string;
  [QuotesCodeMapping.CHANGE_PERCENT_V2]: string;
  [QuotesCodeMapping.CHANGE_V2]: string;
};

enum ApiFormat {
  D_FORMAT = 'D_FORMAT',
  C_FORMAT = 'C_FORMAT'
}

type TabsProps = { tab: string; subTab: string };
type ListProps = TabsProps & { search: string; userSaveList: string[] };

const ReactTable = dynamic(() => import('@/components/atoms/ReactTable'), {
  ssr: false,
  loading: () => <ForexListSkeleton />
});

const gtmEvent = {
  [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_BUTTON,
  [GTMEventKey.SECTION]: '%p_看看匯率自選'
};

const List = ({ tab, subTab, search, userSaveList }: ListProps) => {
  const { setComponent, setSymbol } = useFixedCard();
  const { data: res } = useQuery({
    queryKey: ['forex-list-chart', tab, search ? SubTab.ALL : subTab],
    queryFn: getForexList,
    staleTime: 1000 * 60,
    gcTime: 1000 * 60
  });

  const data = res?.data?.items || [];
  const columns = tab === ApiFormat.D_FORMAT ? mobileQuotesColumns : mobilePerformanceColumns;
  const columnsWithSave = [saveColumn(userSaveList, gtmEvent), ...columns];
  const onClickRow = useCallback((row: DFormat) => {
    setComponent('RateDetails');
    setSymbol(row[QuotesCodeMapping.SYMBOL]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className='mt-4 px-3 pb-4'>
      <ReactTable
        data={data}
        columns={columnsWithSave}
        enablePagination
        Skeleton={<ForexListSkeleton />}
        className='h-[62px]'
        headerClassName='h-[46px]'
        search={search}
        onClick={onClickRow}
      />
    </div>
  );
};

const ForexListMobile = () => {
  const [searchWord, setSearchWord] = useState<string>('');
  const isSearch = searchWord !== '';
  const inputRef = useRef<{
    focus: () => void;
    clear: () => void;
  } | null>(null);

  const onClearSearch = () => {
    setSearchWord('');
    inputRef?.current?.clear();
  };

  const onSearch = (value: string) => {
    sendGTM({
      [GTMEventKey.EVENT_NAME]: CustomEventName.SEARCH,
      [GTMEventKey.SECTION]: '%p_看看匯率',
      [GTMEventKey.CLICK_ITEM]: value
    });

    setSearchWord(value);
  };

  // 取得使用者自選列表
  const { data: userSaveList } = useGetUserSave();

  return (
    <div className='w-full'>
      <Section
        title='看看匯率'
        enabledGTM
        rightContent={<CurrencySearchInput onSearch={onSearch} className='mr-2' ref={inputRef} />}
      />
      <ListTabs tabStyle='px-4' isSearch={isSearch} clearSearch={onClearSearch}>
        {(props: TabsProps) => <List {...props} search={searchWord} userSaveList={userSaveList} />}
      </ListTabs>
    </div>
  );
};

export default ForexListMobile;
